import React, { useEffect, useRef, useState } from "react";
import { DateRange } from 'react-date-range';
import { format, parseISO, isValid } from 'date-fns'
import Popover from '@mui/material/Popover';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { useDate } from "./DateContext";
import { Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GetParam from "../api/GetParam";

const DatePicker = () => {
    const { selectedDate, setSelectedDate } = useDate();
    const [selected, setSelected] = useState("day");
    const [timesClicked, setTimesClicked] = useState(0);
    const navigate = useNavigate()
    const location = useLocation()
    const dateRange = GetParam('dateRange', location)

    // Listens for changes in url and update daterange if they dont match
    useEffect(() => {
        if (selectedDate[0].dateString === dateRange) {
            return
        } else {
            // Split url parameter at '..'
            const dates = dateRange.split('..')
            // Check if dates exist on calendar
            const date1Valid = isValid(new Date(dates[0]))
            const date2Valid = isValid(new Date(dates[1]))
            if (date1Valid) {
                if (date2Valid) {
                    // If both dates are valid set new date as selectedDate
                    const startDate = new Date(parseISO(dates[0]));
                    const endDate = new Date(parseISO(dates[1]));
                    setSelectedDate([
                        {
                            startDate: startDate,
                            endDate: endDate,
                            key: "selection",
                            dateString: `${dates[0]}..${dates[1]}`
                        }
                    ])
                } else {
                    // If second date is invalid set date to lastweek
                    setDateLastWeek()
                }
            } else {
                // If first date is invalid set date to lastweek
                setDateLastWeek()
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange])

    const setDateToday = () => {
        const today = new Date();
        const todayFormat = format(today, 'yyyy-MM-dd');
        setSelectedDate([
            {
                startDate: today,
                endDate: today,
                key: "selection",
                dateString: `${todayFormat}..${todayFormat}`
            }
        ]);
        navigate(location.pathname + '?dateRange=' + todayFormat + '..' + todayFormat)
    }

    const setDateYesterday = () => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yesterdayFormat = format(yesterday, 'yyyy-MM-dd');
        setSelectedDate([{
            startDate: yesterday,
            endDate: yesterday,
            key: "selection",
            dateString: `${yesterdayFormat}..${yesterdayFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + yesterdayFormat + '..' + yesterdayFormat)
    }

    function setDateThisWeek() {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Start of the current week (Sunday).
        const startOfWeekFormat = format(startOfWeek, 'yyyy-MM-dd');
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // End of the current week (Saturday).
        const endOfWeekFormat = format(endOfWeek, 'yyyy-MM-dd');
        setSelectedDate([{
            startDate: startOfWeek,
            endDate: endOfWeek,
            key: "selection",
            dateString: `${startOfWeekFormat}..${endOfWeekFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfWeekFormat + '..' + endOfWeekFormat)
    }

    function setDateLastWeek() {
        const today = new Date();
        const startOfPreviousWeek = new Date(today);
        startOfPreviousWeek.setDate(today.getDate() - today.getDay() - 7); // Start of the previous week (Sunday).
        const startOfPreviousWeekFormat = format(startOfPreviousWeek, 'yyyy-MM-dd');
        const endOfPreviousWeek = new Date(today);
        endOfPreviousWeek.setDate(today.getDate() - today.getDay() - 1); // End of the previous week (Saturday).
        const endOfPreviousWeekFormat = format(endOfPreviousWeek, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfPreviousWeek,
            endDate: endOfPreviousWeek,
            key: "selection",
            dateString: `${startOfPreviousWeekFormat}..${endOfPreviousWeekFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfPreviousWeekFormat + '..' + endOfPreviousWeekFormat)
    }

    function setDateThisMonth() {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const startOfMonthFormat = format(startOfMonth, 'yyyy-MM-dd');
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const endOfMonthFormat = format(endOfMonth, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfMonth,
            endDate: endOfMonth,
            key: "selection",
            dateString: `${startOfMonthFormat}..${endOfMonthFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfMonthFormat + '..' + endOfMonthFormat)
    }

    function setDatePreviousMonth() {
        const today = new Date();
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const startOfPreviousMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        const startOfPreviousMonthFormat = format(startOfPreviousMonth, 'yyyy-MM-dd');
        const endOfPreviousMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
        const endOfPreviousMonthFormat = format(endOfPreviousMonth, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfPreviousMonth,
            endDate: endOfPreviousMonth,
            key: "selection",
            dateString: `${startOfPreviousMonthFormat}..${endOfPreviousMonthFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfPreviousMonthFormat + '..' + endOfPreviousMonthFormat)
    }

    function setDateFirstQuarter() {
        const today = new Date();
        const startOfQuarter = new Date(today.getFullYear(), 0, 1); // January 1st
        const startOfQuarterFormat = format(startOfQuarter, 'yyyy-MM-dd');
        const endOfQuarter = new Date(today.getFullYear(), 2, 31); // March 31st
        const endOfQuarterFormat = format(endOfQuarter, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfQuarter,
            endDate: endOfQuarter,
            key: "selection",
            dateString: `${startOfQuarterFormat}..${endOfQuarterFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfQuarterFormat + '..' + endOfQuarterFormat)
    }

    function setDateSecondQuarter() {
        const today = new Date();
        const startOfQuarter = new Date(today.getFullYear(), 3, 1); // April 1st
        const startOfQuarterFormat = format(startOfQuarter, 'yyyy-MM-dd');
        const endOfQuarter = new Date(today.getFullYear(), 5, 30); // June 30th
        const endOfQuarterFormat = format(endOfQuarter, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfQuarter,
            endDate: endOfQuarter,
            key: "selection",
            dateString: `${startOfQuarterFormat}..${endOfQuarterFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfQuarterFormat + '..' + endOfQuarterFormat)
    }

    function setDateThirdQuarter() {
        const today = new Date();
        const startOfQuarter = new Date(today.getFullYear(), 6, 1); // July 1st
        const startOfQuarterFormat = format(startOfQuarter, 'yyyy-MM-dd');
        const endOfQuarter = new Date(today.getFullYear(), 8, 30); // September 30th
        const endOfQuarterFormat = format(endOfQuarter, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfQuarter,
            endDate: endOfQuarter,
            key: "selection",
            dateString: `${startOfQuarterFormat}..${endOfQuarterFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfQuarterFormat + '..' + endOfQuarterFormat)
    }

    function setDateFourthQuarter() {
        const today = new Date();
        const startOfQuarter = new Date(today.getFullYear(), 9, 1); // October 1st
        const startOfQuarterFormat = format(startOfQuarter, 'yyyy-MM-dd');
        const endOfQuarter = new Date(today.getFullYear(), 11, 31); // December 31st
        const endOfQuarterFormat = format(endOfQuarter, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfQuarter,
            endDate: endOfQuarter,
            key: "selection",
            dateString: `${startOfQuarterFormat}..${endOfQuarterFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfQuarterFormat + '..' + endOfQuarterFormat)
    }

    function setDateThisYear() {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1); // January 1st
        const startOfYearFormat = format(startOfYear, 'yyyy-MM-dd');
        const endOfYear = new Date(today.getFullYear(), 11, 31); // December 31st
        const endOfYearFormat = format(endOfYear, 'yyyy-MM-dd');

        setSelectedDate([{
            startDate: startOfYear,
            endDate: endOfYear,
            key: "selection",
            dateString: `${startOfYearFormat}..${endOfYearFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfYearFormat + '..' + endOfYearFormat)
    }

    function setDateLastYear() {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear() - 1, 0, 1); // January 1st of the previous year
        const startOfYearFormat = format(startOfYear, 'yyyy-MM-dd');
        const endOfYear = new Date(today.getFullYear() - 1, 11, 31); // December 31st of the previous year
        const endOfYearFormat = format(endOfYear, 'yyyy-MM-dd');
        setSelectedDate([{
            startDate: startOfYear,
            endDate: endOfYear,
            key: "selection",
            dateString: `${startOfYearFormat}..${endOfYearFormat}`
        }])
        navigate(location.pathname + '?dateRange=' + startOfYearFormat + '..' + endOfYearFormat)
    }

    const DatePickerShortcuts = (selected) => {
        switch (selected) {
            case "day":
                return (
                    <>
                        <Button onClick={setDateToday}>Vandaag</Button>
                        <Button onClick={setDateYesterday}>Gisteren</Button>
                    </>
                );
            case "week":
                return (
                    <>
                        <Button onClick={setDateThisWeek}>Deze week</Button>
                        <Button onClick={setDateLastWeek}>Vorige week</Button>
                    </>
                );
            case "month":
                return (
                    <>
                        <Button onClick={setDateThisMonth}>Deze maand</Button>
                        <Button onClick={setDatePreviousMonth}>Vorige maand</Button>
                    </>
                );
            case "quarter":
                return (
                    <>
                        <Button onClick={setDateFirstQuarter}>1e kwartaal</Button>
                        <Button onClick={setDateSecondQuarter}>2e kwartaal</Button>
                        <Button onClick={setDateThirdQuarter}>3e kwartaal</Button>
                        <Button onClick={setDateFourthQuarter}>4e kwartaal</Button>
                    </>
                );
            case "year":
                return (
                    <>
                        <Button onClick={setDateThisYear}>Dit jaar</Button>
                        <Button onClick={setDateLastYear}>Vorig jaar</Button>
                    </>
                );
            default:
                return null;
        }
    }

    const [anchorEl, setAnchorEl] = useState(null);
    const refOne = useRef(null);

    useEffect(() => {
        document.addEventListener("keydown", hideOnEscape, true);
        document.addEventListener("click", hideOnClickOutside, true);
    }, []);

    const hideOnEscape = (e) => {
        if (e.key === "Escape") {
            setAnchorEl(null);
        }
    }

    const hideOnClickOutside = (e) => {
        if (refOne.current && !refOne.current.contains(e.target)) {
            setAnchorEl(null);
        }
    }

    const handleOpenPopover = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    return (
        <div className="CallendarWrap">
            <Button
                onClick={handleOpenPopover}
                disableRipple
                sx={{
                    width: '250px',
                    height: '30px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    "&:hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
            >
                <Typography sx={{ color:'#141414', fontFamily:'Source Sans Pro,sans-serif !important' }}>{(format(selectedDate[0].startDate, "yyyy-MMM-dd") + ' t/m ' + format(selectedDate[0].endDate, "yyyy-MMM-dd"))}</Typography>
            </Button>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    "& .rdrDateDisplayItemActive": {
                        borderColor:'#00ab74 !important'
                    },
                    "& .rdrDayToday .rdrDayNumber span:after":{
                        backgroundColor:'#00ab74 !important'
                    }
                }}
            >
                <div ref={refOne}>
                    <div style={{ display: 'flex' }}>
                        <Button style={{ marginRight: '3px' }} onClick={() => setSelected("day")}>Dag</Button>
                        <Button style={{ marginRight: '3px' }} onClick={() => setSelected("week")}>Week</Button>
                        <Button style={{ marginRight: '3px' }} onClick={() => setSelected("month")}>Maand</Button>
                        <Button style={{ marginRight: '3px' }} onClick={() => setSelected("quarter")}>Kwartaal</Button>
                        <Button style={{ marginRight: '3px' }} onClick={() => setSelected("year")}>Jaar</Button>
                    </div>

                    {/* Date Range Picker */}
                    <div style={{ flexDirection: 'column' }}>
                        <DateRange
                            rangeColors={['#00ab74']}
                            onChange={item => {
                                setTimesClicked(timesClicked + 1)
                                if (timesClicked === 1) {
                                    setTimesClicked(0)
                                }
                                setSelectedDate([{
                                    startDate: item.selection.startDate,
                                    endDate: item.selection.endDate,
                                    key: "selection",
                                    dateString: format(item.selection.startDate, 'yyyy-MM-dd') + '..' + format(item.selection.endDate, 'yyyy-MM-dd')
                                }])
                                navigate(location.pathname + '?dateRange=' + format(item.selection.startDate, 'yyyy-MM-dd') + '..' + format(item.selection.endDate, 'yyyy-MM-dd'))
                            }}
                            editableDateInputs={true}
                            moveRangeOnFirstSelection={false}
                            ranges={selectedDate}
                            months={1}
                            direction="horizontal"
                            className="calendarElement"
                        />
                        {DatePickerShortcuts(selected)}
                    </div>
                </div>
            </Popover>
        </div>
    );
}

export default DatePicker;
